import { InfiniteData, QueryFilters, useInfiniteQuery, useMutation } from '@tanstack/react-query';
import {
  AddUserRequest,
  DeleteUserBusinessRequest,
  DeleteUserRequest,
  GetUserListResponse,
  GetUsersListV1UsersGetParams,
  UserRole,
} from 'compliance-api/generated';
import { FormikErrors, FormikValues } from 'formik';
import {
  DeleteUserComplianceRoleRequest,
  KeycloakRole,
  UpdateUserByComplianceAdminRequest,
  UpdateUserComplianceRoleRequest,
} from 'identity-center-api';
import toast from 'react-hot-toast';
import { api, identityCenterApi } from '@/modules/common/api/index.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY } from '@/modules/admin-dashboard/queries/department.ts';

export const USERS_LIST_QUERY_KEY = 'vestra-compliance/my-users';

export const getUsersListQueryKey = (
  params: Omit<GetUsersListV1UsersGetParams, 'page'>,
  cacheKey: string | undefined,
  businessId: number | null,
) => [USERS_LIST_QUERY_KEY, businessId, JSON.stringify(params), cacheKey] as const;

export const useUsersListQuery = (
  payload: GetUsersListV1UsersGetParams,
  options?: {
    disabled?: boolean;
    cacheKey?: string;
  },
) => {
  const { disabled = false, cacheKey } = options || {};
  const { page, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetUserListResponse>({
    queryKey: getUsersListQueryKey(data, cacheKey, businessId),
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getUsersListV1UsersGet({
        ...payload,
        page: typeof pageParam === 'number' && pageParam ? pageParam : pageParam?.page || page,
        filter_by_name: pageParam?.search || payload.filter_by_name,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useDeleteUsersFromDepartmentMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: DeleteUserRequest) => {
      const response = await withErrorHandling(api.v1.removeUserV1UsersDelete(payload), {
        loading: 'Loading...',
        success: 'The user has been successfully deleted',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([USERS_LIST_QUERY_KEY]);
    },
  });
};

export const useAddUserMutation = ({
  setErrors,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: AddUserRequest) => {
      const response = await withErrorHandling(api.v1.addUserV1UsersAddUserPost(payload), {
        loading: 'Loading...',
        success: 'The user has been successfully created',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([USERS_LIST_QUERY_KEY]);
    },
  });
};

export const useDeleteUsersFromBusinessMutation = ({
  setErrors,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: DeleteUserBusinessRequest) => {
      const response = await withErrorHandling(api.v1.deleteFromBusinessV1BusinessPost(payload), {
        loading: 'Loading...',
        success: 'The user has been successfully deleted from this business',
        setErrors,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY]);
      queryClient.invalidateQueries([USERS_LIST_QUERY_KEY]);
    },
  });
};

export const useUpdateUserByComplianceAdminMutation = (businessId: number) => {
  const userListQueryFilters: QueryFilters = {
    queryKey: [USERS_LIST_QUERY_KEY, businessId],
  };

  return useMutation({
    mutationFn: async ({
      userId,
      profile,
      rolesToAdd,
      rolesToRemove,
    }: {
      userId: string;
      profile?: UpdateUserByComplianceAdminRequest;
      rolesToAdd?: UpdateUserComplianceRoleRequest[];
      rolesToRemove?: DeleteUserComplianceRoleRequest[];
    }) => {
      if (profile) {
        await withErrorHandling(
          identityCenterApi.v1.updateUserProfileByComplianceAdminV1UsersUserIdProfilePut(
            { userId },
            profile,
          ),
          {
            loading: 'Updating user...',
            success: '',
          },
        );
      }

      for (const role of rolesToAdd ?? []) {
        await withErrorHandling(
          identityCenterApi.v1.updateUserComplianceRoleV1UsersUserRolePut(role),
          {
            loading: 'Updating role...',
            success: '',
          },
        );
      }

      for (const role of rolesToRemove ?? []) {
        await withErrorHandling(
          identityCenterApi.v1.deleteUserComplianceRoleV1UsersUserRoleDelete(role),
          {
            loading: 'Updating role...',
            success: '',
          },
        );
      }

      return { userId, profile, rolesToAdd, rolesToRemove };
    },
    onSuccess: ({ userId, profile, rolesToAdd, rolesToRemove }) => {
      queryClient.setQueriesData<InfiniteData<GetUserListResponse>>(
        userListQueryFilters,
        currentData => {
          if (!currentData) return currentData;

          return {
            ...currentData,
            pages: currentData.pages.map(page => ({
              ...page,
              data: page.data.map(user =>
                user.keycloak_user_id === userId
                  ? {
                      ...user,
                      first_name: profile?.firstName ?? user.first_name,
                      last_name: profile?.lastName ?? user.last_name,
                      roles: Array.from(
                        new Set([
                          ...(user.roles ?? []),
                          ...(rolesToAdd ?? []).map(role =>
                            role.role === KeycloakRole.ComplianceAdmin
                              ? UserRole.Admin
                              : UserRole.User,
                          ),
                        ]),
                      ).filter(
                        role =>
                          !(rolesToRemove ?? []).some(removedRole =>
                            removedRole.role === KeycloakRole.ComplianceAdmin
                              ? role === UserRole.Admin
                              : role === UserRole.User,
                          ),
                      ),
                    }
                  : user,
              ),
            })),
          };
        },
      );
    },
    onError: () => {
      toast.error('Some changes may have been saved. User data has been refreshed.');
    },
    onSettled: (_data, error) => {
      if (error) {
        queryClient.invalidateQueries(userListQueryFilters);
      }
    },
  });
};
